import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Phone,
  UserPlus,
  AlertCircle,
} from 'lucide-react';
import '../styles/auth.css';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { googleStart, loginStart, loginVerify, sendOtp, verifyOtp } from '../../src/api/auth';
import googleIcon from '../../src/assets/icons/google-icon-logo-svgrepo-com.svg';
import laborConnectWordmark from '../../src/assets/LaborConnect Wordmark.png';
import { TURNSTILE_SITE_KEY } from '../../src/config';
import { consumeTurnstileTokenForAction } from '../../src/utils/turnstileFlow';

const TRUSTED_DEVICE_STORAGE_KEY = 'alu_trusted_device_token';

export default function LoginPage({ onLoginSuccess, onCreateAccount, onBack }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [method, setMethod] = useState('email');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [step, setStep] = useState('credentials');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [resendMsLeft, setResendMsLeft] = useState(0);
  const RESEND_COOLDOWN_MS = Number(process.env.REACT_APP_OTP_RESEND_COOLDOWN_MS ?? 60000);
  const turnstileEnabled = Boolean(TURNSTILE_SITE_KEY);
  const takeLoginTurnstileToken = () => (turnstileEnabled ? consumeTurnstileTokenForAction('login_start') : '');
  const redirectToHumanCheck = () => {
    navigate('/human-check', {
      state: {
        nextPath: '/login',
        requiredAction: 'login_start',
        resumeState: {
          identifier,
          password,
          method,
          step,
          rememberMe,
          otpCode,
          resendMsLeft,
        },
      },
    });
  };
  const isTurnstileChallengeError = (err) => {
    if (!turnstileEnabled) return false;
    const status = Number(err?.response?.status || 0);
    const message = String(err?.response?.data?.message || '').toLowerCase();
    return (status === 400 || status === 403)
      && message.includes('security verification');
  };

  useEffect(() => {
    const resumeState = location.state?.resumeState;
    if (!resumeState) return;

    if (typeof resumeState.identifier === 'string') setIdentifier(resumeState.identifier);
    if (typeof resumeState.password === 'string') setPassword(resumeState.password);
    if (resumeState.method === 'email' || resumeState.method === 'mobile') setMethod(resumeState.method);
    if (resumeState.step === 'credentials' || resumeState.step === 'otp') setStep(resumeState.step);
    if (typeof resumeState.otpCode === 'string') setOtpCode(resumeState.otpCode);
    if (typeof resumeState.rememberMe === 'boolean') setRememberMe(resumeState.rememberMe);
    if (Number.isFinite(resumeState.resendMsLeft)) setResendMsLeft(Math.max(0, resumeState.resendMsLeft));

    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (resendMsLeft <= 0) return undefined;
    const interval = setInterval(() => {
      setResendMsLeft((prev) => Math.max(prev - 1000, 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [resendMsLeft]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      const payload = event.data;
      if (payload?.type === 'google-auth' && payload.user) {
        onLoginSuccess({
          user: payload.user,
          token: payload.token ?? null,
          expiresAt: payload.expiresAt ?? null,
          rememberMe: Boolean(payload.rememberMe),
        });
      }
      if (payload?.type === 'google-auth-error') {
        setFormError(payload.message || 'Google login failed. Please try again.');
      }
      if (payload?.type === 'google-auth-setup' && payload.token) {
        navigate(`/register?googleSetupToken=${encodeURIComponent(payload.token)}`);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [navigate, onLoginSuccess]);

  const clearFieldError = (name) => {
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  // Auto-detect input type based on value
  const detectInputType = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return 'unknown';
    if (trimmed.includes('@')) return 'email';
    // Looks like a PH phone number (starts with 0, +, or 63)
    const stripped = trimmed.replace(/[\s\-()]/g, '');
    if (/^(\+?63|0)\d/.test(stripped)) return 'phone';
    // If it's all digits (user is typing a number), treat as phone
    if (/^\+?\d+$/.test(stripped)) return 'phone';
    return 'email';
  };

  const isValidPHPhone = (value) => {
    const stripped = value.trim().replace(/[\s\-()]/g, '');
    // 9XXXXXXXXX (10 digits, no leading 0)
    if (/^9\d{9}$/.test(stripped)) return true;
    // 09XXXXXXXXX (11 digits)
    if (/^09\d{9}$/.test(stripped)) return true;
    // +639XXXXXXXXX (13 chars with +)
    if (/^\+639\d{9}$/.test(stripped)) return true;
    // 639XXXXXXXXX (12 digits)
    if (/^639\d{9}$/.test(stripped)) return true;
    return false;
  };

  const detectedType = detectInputType(identifier);
  const isEmailMethod = method === 'email';

  const validate = ({ requirePassword = true } = {}) => {
    const errors = {};
    const trimmedIdentifier = identifier.trim();

    if (!trimmedIdentifier) {
      errors.identifier = isEmailMethod
        ? 'Please enter your email address.'
        : 'Please enter your mobile number.';
    } else if (isEmailMethod) {
      if (detectedType !== 'email' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedIdentifier)) {
        errors.identifier = 'Enter a valid email address.';
      }
    } else {
      if (!isValidPHPhone(trimmedIdentifier)) {
        errors.identifier = 'Enter a valid PH mobile number (9, 09, 63, or +63 format).';
      }
    }

    if (isEmailMethod && requirePassword && !password.trim()) {
      errors.password = 'Password is required to continue.';
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setFormError('Please fix the highlighted fields.');
      return false;
    }
    setFormError('');
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    if (!validate({ requirePassword: isEmailMethod })) return;
    const turnstileToken = takeLoginTurnstileToken();

    setLoading(true);
    setFormError('');
    try {
      if (!isEmailMethod) {
        await sendOtp({
          phone: identifier.trim(),
          turnstileToken: turnstileToken || undefined,
        });
        setStep('otp');
        setResendMsLeft(RESEND_COOLDOWN_MS);
        return;
      }

      const trustedDeviceToken = localStorage.getItem(TRUSTED_DEVICE_STORAGE_KEY);
      const { data } = await loginStart({
        identifier: identifier.trim(),
        password,
        trustedDeviceToken,
        turnstileToken: turnstileToken || undefined,
      });

      if (data?.user && data?.token && data?.requiresOtp !== true) {
        if (data?.trustedDeviceToken) {
          localStorage.setItem(TRUSTED_DEVICE_STORAGE_KEY, data.trustedDeviceToken);
        }
        onLoginSuccess({
          user: data.user,
          token: data.token,
          expiresAt: data.expiresAt,
          rememberMe: true,
        });
        return;
      }

      setStep('otp');
      setResendMsLeft(RESEND_COOLDOWN_MS);
    } catch (err) {
      if (isTurnstileChallengeError(err)) {
        redirectToHumanCheck();
        return;
      }
      const status = err.response?.status;
      const apiMessage = err.response?.data?.message;
      const apiDetail = err.response?.data?.error;
      if (!isEmailMethod && status === 404) {
        setFormError('No account found for that mobile number.');
      } else if (status === 404 || status === 401) {
        setFormError('Invalid email or password.');
      } else if (status === 403) {
        setFormError('Your account still needs verification. Please complete the email verification steps.');
      } else if (apiMessage) {
        setFormError(apiDetail ? `${apiMessage} (${apiDetail})` : apiMessage);
      } else {
        setFormError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (event) => {
    event.preventDefault();
    if (loading) return;
    const turnstileToken = takeLoginTurnstileToken();
    const trimmedCode = otpCode.trim();
    if (!/^[0-9]{6}$/.test(trimmedCode)) {
      setFormError(
        isEmailMethod
          ? 'Enter the 6-digit code sent to your email.'
          : 'Enter the 6-digit OTP sent to your mobile number.',
      );
      return;
    }

    setLoading(true);
    setFormError('');
    try {
      const { data } = isEmailMethod
        ? await loginVerify({
            identifier: identifier.trim(),
            code: trimmedCode,
            rememberMe,
            turnstileToken: turnstileToken || undefined,
          })
        : await verifyOtp({
            phone: identifier.trim(),
            code: trimmedCode,
            rememberMe,
            turnstileToken: turnstileToken || undefined,
          });

      if (rememberMe && data?.trustedDeviceToken) {
        localStorage.setItem(TRUSTED_DEVICE_STORAGE_KEY, data.trustedDeviceToken);
      } else if (!rememberMe) {
        localStorage.removeItem(TRUSTED_DEVICE_STORAGE_KEY);
      }

      onLoginSuccess({
        user: data.user,
        token: data.token,
        expiresAt: data.expiresAt,
        rememberMe,
      });
    } catch (err) {
      if (isTurnstileChallengeError(err)) {
        redirectToHumanCheck();
        return;
      }
      const apiMessage = err.response?.data?.message;
      const apiDetail = err.response?.data?.error;
      if (apiMessage) {
        setFormError(apiDetail ? `${apiMessage} (${apiDetail})` : apiMessage);
      } else {
        setFormError(err.message || 'Verification failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (loading || resendMsLeft > 0) return;
    if (!validate({ requirePassword: isEmailMethod })) return;
    const turnstileToken = takeLoginTurnstileToken();

    setLoading(true);
    setFormError('');
    try {
      if (isEmailMethod) {
        await loginStart({
          identifier: identifier.trim(),
          password,
          turnstileToken: turnstileToken || undefined,
        });
      } else {
        await sendOtp({
          phone: identifier.trim(),
          turnstileToken: turnstileToken || undefined,
        });
      }
      setResendMsLeft(RESEND_COOLDOWN_MS);
    } catch (err) {
      if (isTurnstileChallengeError(err)) {
        redirectToHumanCheck();
        return;
      }
      setFormError(err.response?.data?.message || 'Failed to resend code.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      onCreateAccount();
    }
  };

  const handleGoogleContinue = async () => {
    if (loading) return;
    const turnstileToken = takeLoginTurnstileToken();

    setLoading(true);
    setFormError('');
    try {
      const origin = window.location?.origin || '';
      const { data } = await googleStart({
        origin,
        turnstileToken: turnstileToken || undefined,
      });

      const authUrl = typeof data?.authUrl === 'string' ? data.authUrl : '';
      if (!authUrl) {
        throw new Error('Failed to start Google sign-in.');
      }

      const popup = window.open(
        authUrl,
        'google-oauth',
        'width=500,height=650,menubar=no,toolbar=no,location=no,status=no'
      );
      if (!popup) {
        window.location.href = authUrl;
      }
    } catch (err) {
      if (isTurnstileChallengeError(err)) {
        redirectToHumanCheck();
        return;
      }
      const apiMessage = err.response?.data?.message;
      setFormError(apiMessage || err.message || 'Google login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <header className="auth-page__header">
        <div className="auth-page__header-inner">
          <button type="button" className="auth-page__back" onClick={handleBack} aria-label="Back to landing">
            <ArrowLeft size={18} />
          </button>
          <div className="auth-page__title">
            <div>
              <h1>Login</h1>
              <p>Secure member access</p>
            </div>
          </div>
        </div>
      </header>

      <main className="auth-page__body">
        <div className="auth-page__inner">
          <section className="auth-page__intro">
            <div className="auth-page__badge">Secure Member Access</div>
            <h2>
              Welcome back to
              <img src={laborConnectWordmark} alt="LaborConnect" className="auth-page__wordmark" />
            </h2>
            <p>
              Sign in using your registered email or mobile number to access your Digital ID, submit requests and
              assistance concerns, manage venue bookings, and use AI-powered features. Your login is protected using
              industry standard encryption.
            </p>
          </section>

          <section className="auth-page__form">
            <div className="auth-card">
              <div className="auth-card__toggle">
                <button
                  type="button"
                  className={method === 'email' ? 'auth-card__toggle-btn auth-card__toggle-btn--active' : 'auth-card__toggle-btn'}
                  onClick={() => {
                    setMethod('email');
                    setStep('credentials');
                    setOtpCode('');
                    setResendMsLeft(0);
                    setFormError('');
                    setFieldErrors({});
                  }}
                >
                  <Mail size={16} />
                  Email Login
                </button>
                <button
                  type="button"
                  className={method === 'mobile' ? 'auth-card__toggle-btn auth-card__toggle-btn--active' : 'auth-card__toggle-btn'}
                  onClick={() => {
                    setMethod('mobile');
                    setStep('credentials');
                    setOtpCode('');
                    setResendMsLeft(0);
                    setFormError('');
                    setFieldErrors({});
                  }}
                >
                  <Phone size={16} />
                  Phone Login
                </button>
              </div>

              <form onSubmit={step === 'credentials' ? handleSubmit : handleVerifyOtp} className="auth-card__form">
                {formError && (
                  <div className="auth-card__alert auth-card__alert--error">
                    <AlertCircle size={16} />
                    <span>{formError}</span>
                  </div>
                )}

                {step === 'credentials' && (
                  <>
                    <div className="auth-card__field">
                      <label htmlFor="identifier">{method === 'email' ? 'Email address' : 'Phone number'}</label>
                      <div className={`auth-card__input ${fieldErrors.identifier ? 'auth-card__input--error' : ''}`}>
                        {method === 'email' ? <Mail size={18} /> : <Phone size={18} />}
                        <input
                          id="identifier"
                          type={method === 'email' ? 'email' : 'tel'}
                          placeholder={method === 'email' ? 'juan.delacruz@gmail.com' : '+63 900 000 0000'}
                          value={identifier}
                          onChange={(event) => {
                            setIdentifier(event.target.value);
                            clearFieldError('identifier');
                            setFormError('');
                          }}
                          required
                        />
                      </div>
                      {fieldErrors.identifier && (
                        <div className="auth-card__field-error">
                          <AlertCircle size={14} />
                          <span>{fieldErrors.identifier}</span>
                        </div>
                      )}
                    </div>

                    {isEmailMethod && (
                      <div className="auth-card__field">
                        <label htmlFor="password">Password</label>
                        <div className={`auth-card__input ${fieldErrors.password ? 'auth-card__input--error' : ''}`}>
                          <Lock size={18} />
                          <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter your secure password"
                            value={password}
                            onChange={(event) => {
                              setPassword(event.target.value);
                              clearFieldError('password');
                              setFormError('');
                            }}
                            required
                          />
                          <button
                            type="button"
                            className="auth-card__password-toggle"
                            onClick={() => setShowPassword((prev) => !prev)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        {fieldErrors.password && (
                          <div className="auth-card__field-error">
                            <AlertCircle size={14} />
                            <span>{fieldErrors.password}</span>
                          </div>
                        )}
                        <div style={{ textAlign: 'right', marginTop: -4 }}>
                          <Link to="/forgot-password" style={{
                            fontSize: 13, color: '#2563eb', textDecoration: 'none', fontWeight: 600
                          }}>
                            Forgot password?
                          </Link>
                        </div>
                      </div>
                    )}

                    <label className="auth-remember">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(event) => setRememberMe(event.target.checked)}
                      />
                      <span>Remember me for 30 days on this device</span>
                    </label>

                    <button type="submit" className="auth-card__submit" disabled={loading}>
                      {loading ? (isEmailMethod ? 'Sending code...' : 'Sending OTP...') : (isEmailMethod ? 'Continue' : 'Send OTP')}
                      {!loading && <ArrowRight size={18} />}
                    </button>

                    {isEmailMethod && (
                      <>
                        <div className="auth-card__divider">
                          <span>or</span>
                        </div>

                        <button
                          type="button"
                          className="auth-card__google-btn"
                          onClick={handleGoogleContinue}
                          disabled={loading}
                        >
                          <span className="auth-card__google-icon" aria-hidden="true">
                            <img src={googleIcon} alt="" />
                          </span>
                          Continue with Google
                        </button>
                      </>
                    )}
                  </>
                )}

                {step === 'otp' && (
                  <>
                    <div className="auth-card__field">
                      <label htmlFor="otpCode">{isEmailMethod ? 'Verification Code' : 'OTP Code'}</label>
                      <div className="auth-card__input">
                        <Lock size={18} />
                        <input
                          id="otpCode"
                          type="text"
                          placeholder="000000"
                          value={otpCode}
                          onChange={(event) => {
                            setOtpCode(event.target.value);
                            setFormError('');
                          }}
                          maxLength={6}
                          required
                        />
                      </div>
                      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 8, marginBottom: 0 }}>
                        {isEmailMethod
                          ? 'Enter the 6-digit code sent to your email.'
                          : 'Enter the 6-digit code sent to your mobile number via SMS.'}
                      </p>
                    </div>

                    <button type="submit" className="auth-card__submit" disabled={loading}>
                      {loading ? 'Verifying...' : 'Verify & Sign In'}
                      {!loading && <ArrowRight size={18} />}
                    </button>

                    <button
                      type="button"
                      className="auth-card__back-btn"
                      onClick={handleResend}
                      disabled={loading || resendMsLeft > 0}
                    >
                      {resendMsLeft > 0
                        ? `Resend in ${Math.ceil(resendMsLeft / 1000)}s`
                        : 'Resend Code'}
                    </button>
                  </>
                )}
              </form>

              <div className="auth-card__footer">
                <span>Need an account?</span>
                <button type="button" onClick={onCreateAccount}>
                  <UserPlus size={16} />
                  Start Registration
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>

      <footer className="auth-page__footer">
        <p>Need help? Contact your union representative</p>
        <p>Labor Connect - Luzon regional</p>
      </footer>
    </div>
  );
}

LoginPage.propTypes = {
  onLoginSuccess: PropTypes.func.isRequired,
  onCreateAccount: PropTypes.func.isRequired,
  onBack: PropTypes.func,
};

LoginPage.defaultProps = {
  onBack: null,
};
